import { ContactLabel } from "@common-models/contact-label"
import { _labelSelectSelectOneFieldByQuery, contactsLabelsSelectByQuery, contactsLabelsSelectors, labelSelectors, WaChatSelectors } from "@common/reducers/DBServiceThunks";
import { createSelector } from "@reduxjs/toolkit"

import { CommonRootState } from "@common/types/common-root-state-type"
import { getAllContactIds } from "./helper-functions"
import { dedupeLabelsByType } from "./label-dedupe"

// Base selector for all contact labels - memoized
const selectAllContactLabels = (state: CommonRootState) => contactsLabelsSelectors.selectAll(state);
const selectAllWaChats = (state: CommonRootState) => WaChatSelectors.selectAll(state);
const selectAllLabels = (state: CommonRootState) => labelSelectors.selectAll(state);

// The visible label set for the label bar, de-duplicated and ordered. Memoized so
// the label tabs only re-render when the label entities actually change. Replaces an
// inline query+dedupe in label-top-header: dedupe belongs here (reactive on store
// change) not in the component. Filters out deleted/hidden labels, sorts by `order`,
// then collapses duplicate system labels (see dedupeLabelsByType / #163 / #164).
export const selectVisibleDedupedLabels = createSelector(
    [selectAllLabels],
    (allLabels) => {
        const visible = allLabels
            .filter((label) =>
                (label?.deleted === undefined || label?.deleted === false) &&
                label?.isVisible !== false
            )
            .slice()
            .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
        return dedupeLabelsByType(visible);
    }
);

// The assignable label set for the per-contact label picker (LabelsManagerButton),
// de-duplicated and ordered. Same dedupe as the bar (#164) but keeps hidden labels
// (visibility only controls the bar, not assignability). Memoized so the picker's
// checkbox list stays stable and collapses churn-duplicated system labels.
export const selectAssignableDedupedLabels = createSelector(
    [selectAllLabels],
    (allLabels) => {
        const nonDeleted = allLabels
            .filter((label) => label?.deleted === undefined || label?.deleted === false)
            .slice()
            .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
        return dedupeLabelsByType(nonDeleted);
    }
);

// getContactIdsByLabelId selector - properly memoized
export const getContactIdsByLabelId = createSelector(
    [
        (params: { state: CommonRootState, labelId: string }) => selectAllContactLabels(params.state),
        (params: { state: CommonRootState, labelId: string }) => params.labelId
    ],
    (chatLabels, labelId) => {
        const filtered = chatLabels.filter((chatLabel) => chatLabel.labelId === labelId)
        const uniqueIds = new Set(filtered.map((cl) => cl.waChatId))
        return Array.from(uniqueIds)
    }
)

// getContactLabelsByName selector - properly memoized
export const getContactLabelsByName = createSelector(
    [
        (params: { state: CommonRootState, labelName: string }) => selectAllContactLabels(params.state),
        (params: { state: CommonRootState, labelName: string }) => params.labelName
    ],
    (contactLabels, labelName) => {
        return contactLabels.filter((chatLabel) => chatLabel.labelName === labelName)
    }
)

// getInboxContactIds selector - properly memoized
export const getInboxContactIds = createSelector(
    [selectAllContactLabels],
    (chatLabels) => {
        const inboxLabels = chatLabels.filter((l) => l.labelName === 'inbox')
        const uniqueIds = new Set(inboxLabels.map((l) => l.waChatId))
        return Array.from(uniqueIds)
    }
)

// getUnreadContactIds selector - properly memoized
export const getUnreadContactIds = createSelector(
    [selectAllWaChats],
    (chats) => {
        const unreadChats = chats.filter((c) => c.unreadCount > 0)
        return unreadChats.map((c) => c.id._serialized)
    }
)


// getBadgeNameAndCountByWaChatId - for parameterized selectors, we use a factory pattern
export const getBadgeNameAndCountByWaChatId = createSelector(
    [
        (params: { state: CommonRootState, waChatId: string }) => params?.state?.DBContactsLabelsReducer,
        (params: { state: CommonRootState, waChatId: string }) => params?.state?.DBLabelReducer,
        (params: { state: CommonRootState, waChatId: string }) => params?.waChatId
    ],
    (contactLabelsReducer, labelReducer, waChatId) => {
        if (!waChatId) return { name: null, count: 0, waChatId: null }
        const contactLabelBadges = contactsLabelsSelectByQuery(contactLabelsReducer, {
            waChatId,
            labelType: 'custom'
        })

        if (contactLabelBadges && contactLabelBadges.length > 0) {
            const firstItem = contactLabelBadges[0]
            const icon = _labelSelectSelectOneFieldByQuery(labelReducer, { _id: firstItem?.labelId }, 'icon')
            if (icon) {
                return {
                    name: `${icon} ${firstItem?.labelName}`,
                    count: contactLabelBadges.length,
                    waChatId
                }
            }
        }
        return { name: null, count: 0, waChatId: null }
    }
)

export const getContactBadges = createSelector(
    [
        (params: { state: CommonRootState, waChatId: string }) => params?.state?.DBContactsLabelsReducer,
        (params: { state: CommonRootState, waChatId: string }) => params?.state?.DBLabelReducer,
        (params: { state: CommonRootState, waChatId: string }) => params?.waChatId
    ],
    (contactLabelsReducer, labelReducer, waChatId) => {
        if (!waChatId) return []
        const contactLabels = contactsLabelsSelectByQuery(contactLabelsReducer, {
            waChatId,
            labelType: 'custom'
        })

        return contactLabels.map(cl => {
            const icon = _labelSelectSelectOneFieldByQuery(labelReducer, { _id: cl.labelId }, 'icon')
            return {
                ...cl,
                icon,
            };
        })
    }
)

export const getSelectedContactLabelB = createSelector(
    [
        (params: { state: CommonRootState, waContactId: string, labelName: string }) => selectAllContactLabels(params.state),
        (params: { state: CommonRootState, waContactId: string, labelName: string }) => params.waContactId,
        (params: { state: CommonRootState, waContactId: string, labelName: string }) => params.labelName
    ],
    (contactLabels, waContactId, labelName) => {
        return contactLabels.find((item) => item.labelName === labelName && item?.waContactId === waContactId)
    }
)

// getInboxCount - properly memoized, derives from getInboxContactIds
export const getInboxCount = createSelector(
    [getInboxContactIds],
    (contactListIds) => contactListIds.length
)

export const getContactLabelsCount = createSelector(
    [
        (params: { state: CommonRootState, labelId: string }) => params.state.DBContactsLabelsReducer,
        (params: { state: CommonRootState, labelId: string }) => params.labelId
    ],
    (contactLabelsReducer, labelId) => {
        const contactLabels = contactsLabelsSelectByQuery(contactLabelsReducer, { labelId })
        return contactLabels.length
    }
)

// getOpenIssueContactIds selector - properly memoized
export const getOpenIssueContactIds = createSelector(
    [selectAllContactLabels],
    (chatLabels) => {
        console.log('[getOpenIssueContactIds] Total chatLabels:', chatLabels.length);
        const filtered = chatLabels
            .filter((l) => l.labelName.toLowerCase() === 'open issue' || l.labelName.toLowerCase() === 'needs attention');
        console.log('[getOpenIssueContactIds] Filtered (needs attention/open issue):', filtered.length, filtered.map(l => ({ labelName: l.labelName, waChatId: l.waChatId })));
        const uniqueIds = new Set(filtered.map((l) => l.waChatId))
        return Array.from(uniqueIds)
    }
)

// getStarredContactIds selector - properly memoized
export const getStarredContactIds = createSelector(
    [selectAllContactLabels],
    (chatLabels) => {
        const starredLabels = chatLabels.filter((l) => l.labelName === 'starred');
        const uniqueIds = new Set(starredLabels.map((l) => l.waChatId))
        return Array.from(uniqueIds);
    }
)

// getArchivedContactIds selector - properly memoized
export const getArchivedContactIds = createSelector(
    [selectAllContactLabels],
    (chatLabels) => {
        const uniqueIds = new Set(chatLabels
            .filter((l) => l.labelName === 'archived')
            .map((l) => l.waChatId))
        return Array.from(uniqueIds)
    }
)

// getSnoozedContactIds selector - properly memoized
export const getSnoozedContactIds = createSelector(
    [selectAllContactLabels],
    (chatLabels) => {
        const uniqueIds = new Set(chatLabels
            .filter((l) => l.labelName === 'snoozed')
            .map((l) => l.waChatId))
        return Array.from(uniqueIds)
    }
)
